/**
 * BOARD THEMES. A board is a THEME, not a background.
 *
 * 🚨 **THIS IS AN ARCHITECTURE DECISION ALEX ASKED FOR EXPLICITLY**, 2026-08-14: *"Keep in mind that
 * we will need multiple game boards, and that is part of the idea of campaigns, that each campaign
 * or group of campaigns can have a stylised board. In the future we can also sell NFT boards/skins."*
 *
 * ⭐ **SO THE §3b QUESTION IS ANSWERED UP FRONT: to add a board, how many places change? ONE - a row
 * in `BOARD_THEMES` and its art.** Nothing in the board renderer knows a theme's name, and no screen
 * hardcodes a surface. That is the difference between a product that can sell skins and one where
 * every new board is a code change.
 *
 * ⚠️ **A THEME IS COSMETIC AND MUST STAY THAT WAY.** It may not change a slot count, a zone, a rule
 * or a stat. Seven slots a side is `maxCardsOnTable` from the served config, not a style choice -
 * if a theme could move it, a bought skin would alter the game and that is a different product.
 *
 * ⚠️ **THE DEFAULT IS OUR OWN ARENA AND IT ALREADY EXISTED** (`art-src/make_arena.py`): four realm
 * corners, the stone ring, the compass rose, the gold vine border. It was not invented here.
 */

export interface BoardTheme {
  /** Stable id. Used in the asset path and, later, as the entitlement key for a skin. */
  readonly id: string;
  /** What a player sees it called. */
  readonly name: string;
  /** The arena surface, served from `/art/board/<id>/surface.webp`. */
  readonly surface: string;
  /**
   * How far the arena is inset from the viewport edge, as a fraction.
   * A themed border needs room to breathe; a full-bleed one sets this to 0.
   */
  readonly inset: number;
  /** Dim applied over the surface so cards stay legible on top of it. 0 = none. */
  readonly scrim: number;
}

import { REALM_BOARDS } from "../boardThemes.generated";

/**
 * Every board this build ships.
 *
 * ⭐ **THE NEUTRAL ARENA FIRST, THEN THE REALMS - AND THE REALMS ARE GENERATED.** `Background_All`
 * shows all four realms at once, which is the right board for a match between anybody; a campaign
 * stage happens IN a realm, so it gets that realm alone. The realm rows come from
 * `art-src/make_arena.py --realm`, which writes the row and the art in the same pass, so a row can
 * never name a picture that does not exist.
 */
const BASE_BOARDS: readonly BoardTheme[] = [
  {
    id: "deepnorth",
    name: "The Deep North",
    surface: "/art/board/deepnorth/surface.webp",
    inset: 0.02,
    // ⚠️ The arena is a painting with a lot of contrast in its corners. Without a scrim, a card's
    // rules text sits on molten orange at one end and snow at the other and is unreadable at both.
    scrim: 0.42,
  },
];

export const BOARD_THEMES: readonly BoardTheme[] = [...BASE_BOARDS, ...REALM_BOARDS];

/**
 * The board for a match that belongs to no realm - a duel, a practice game, the ladder.
 *
 * ⚠️ It is also the fallback for a realm whose art this build does not carry yet (The Spirit Veil
 * has no painting: it was never one of the original's four corners). A correct board, not a
 * broken one.
 */
export const DEFAULT_BOARD = BASE_BOARDS[0] as BoardTheme;

/**
 * The board a campaign plays on.
 *
 * ⚠️ **UNKNOWN IDS FALL BACK, THEY DO NOT THROW.** A campaign referencing a theme this build does
 * not ship yet - which is exactly what happens the moment boards are sold or added server-side -
 * must give a player the default board, not a broken match.
 */
export function themeFor(id: string | null | undefined): BoardTheme {
  if (!id) return DEFAULT_BOARD;
  const found = BOARD_THEMES.find((t) => t.id === id);
  if (!found) {
    console.warn(`[board] unknown theme '${id}' - falling back to '${DEFAULT_BOARD.id}'`);
    return DEFAULT_BOARD;
  }
  return found;
}
